import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import translate from "@bimproject/t9n";
import { Suspense, useState } from "react";
import { IoClose, IoTrashOutline } from "react-icons/io5";
import { Link, Outlet, useNavigate, useOutletContext, useParams } from "react-router";
import styled from "styled-components";

import * as UI from "~/src/modules/ui-kit";

import DeleteEmptyProductLines from "../../../../clients/brands/DeleteEmptyProductLines.graphql";
import UpsertProductLine from "../../../../clients/brands/UpsertProductLine.graphql";
import useBrand from "../../../../clients/brands/useBrand";
import useProductLineCounts from "../../../../clients/productSearch/useProductLineCounts";
import { confirmUnsavedChanges } from "../../../../modules/unsaved-changes";

import type { ProductLinesAppOutletContext } from "../../index";


const Styled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    transition: opacity 200ms;

    &:not(:last-child):not(:hover):not(:focus-within) {
        opacity: 0.4;
    }

    ${UI.HeroInput} {
        font-size: 2.4em;
    }

    ol {
        display: flex;
        flex-direction: column;
        gap: 8px;
        list-style: none;
        margin: 0;
        padding: 0;
    }
`;

const Row = styled.li`
    display: flex;
    align-items: center;
    gap: 8px;

    a {
        color: #000;
        display: block;
        flex: 1;
        font-size: 1.2rem;
        overflow: hidden;
        position: relative;
        text-decoration: none;
        text-overflow: ellipsis;
        white-space: nowrap;

        ${UI.FluidAccent2}

        &:hover {
            text-decoration: none;
        }
    }
`;

const OrderButton = styled.button`
    background: none;
    border: none;
    color: #000;
    cursor: pointer;
    font-size: 0.8rem;
    line-height: 1;
    opacity: 0.3;
    padding: 2px 4px;

    &:hover {
        opacity: 1;
    }

    &:disabled {
        cursor: default;
        opacity: 0.1;
    }
`;

const Count = styled.span`
    color: #000;
    font-size: 0.9rem;
    opacity: 0.4;
    white-space: nowrap;
`;

const DeleteButton = styled.button`
    background: none;
    border: none;
    color: #c0392b;
    cursor: pointer;
    display: flex;
    font-size: 1rem;
    line-height: 1;
    opacity: 0.5;
    padding: 2px 4px;

    &:hover {
        opacity: 1;
    }
`;

export default function ProductLines() {
    const [token, setToken] = useState("");
    const { productLineID } = useParams();
    const guard = useOutletContext<ProductLinesAppOutletContext>();

    return (
        <>
            <Styled>
                <UI.HeroInputField>
                    <div>
                        <UI.HeroInput
                            autoFocus={!productLineID}
                            onChange={setToken}
                            placeholder="Search"
                            value={token}
                        />
                        <button onClick={() => setToken("")} type="reset">
                            <IoClose />
                        </button>
                    </div>
                    <span>search Product Lines by name</span>
                </UI.HeroInputField>
                <UI.A as={Link} to="./new">
                    create new product line
                </UI.A>
                <Suspense fallback={<span>loading</span>}>
                    <ProductLineList token={token} guard={guard} />
                </Suspense>
            </Styled>
            <Outlet context={guard} />
        </>
    );
}

function ProductLineList(props) {
    const { token, guard } = props;

    const { brandID, productLineID } = useParams();
    const navigate = useNavigate();
    const brand = useBrand(brandID);

    const counts = useProductLineCounts(brand.data.brand.id);

    const [upsert] = useMutation(gql(UpsertProductLine), {
        refetchQueries: ["Brand"],
    });

    const [deleteEmpty] = useMutation(gql(DeleteEmptyProductLines), {
        refetchQueries: ["Brand"],
    });

    // Counts are only trustworthy once the search-index query has resolved
    // without error; until then we treat emptiness as unknown and disable
    // deletion. The server re-checks live regardless.
    const countsReady = !counts.loading && !counts.error && !!counts.data;
    const countMap = new Map(
        (counts.data?.productLineCounts ?? []).map((c) => [c.productLineID, c.productCount]),
    );
    const countOf = (productLine) => countMap.get(productLine.key) ?? 0;

    const allProductLines = brand.data.brand.productLines
        .slice()
        .sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity));

    const unusedLines = countsReady
        ? allProductLines.filter((productLine) => countOf(productLine) === 0)
        : [];

    async function remove(ids: string[]) {
        if (!ids.length) return;
        await deleteEmpty({ variables: { ids } });
        if (productLineID && ids.includes(productLineID)) navigate(".");
    }

    function handleDelete(productLine) {
        if (countOf(productLine) !== 0) return;
        if (!confirm(`Delete unused product line "${translate(productLine.name) || productLine.key}"?`)) return;
        remove([productLine.id]);
    }

    function handlePurge() {
        if (!unusedLines.length) return;
        if (!confirm(`Delete ${unusedLines.length} unused product line(s)? This cannot be undone.`)) return;
        remove(unusedLines.map((productLine) => productLine.id));
    }

    const productLines = allProductLines.filter((productLine) =>
        [
            productLine.id === token,
            productLine.key?.toLowerCase?.()?.includes?.(token.toLowerCase()),
            productLine.name.some((translation) =>
                translation.text.toLowerCase().includes(token.toLowerCase())),
        ].some(Boolean));

    function rebalance(
        items: typeof allProductLines,
        movedIndex: number,
        targetIndex: number,
    ) {
        const reordered = [...items];
        const [moved] = reordered.splice(movedIndex, 1);
        reordered.splice(targetIndex, 0, moved);
        reordered.forEach((pl, i) => {
            upsert({
                variables: { productLine: { id: pl.id, position: i + 1 } },
            });
        });
    }

    function move(index: number, direction: "up" | "down") {
        const items = allProductLines;
        const item = items[index];

        if (direction === "up" && index === 0) return;
        if (direction === "down" && index === items.length - 1) return;

        if (direction === "up") {
            const prev = items[index - 1];
            const prevPrev = items[index - 2];

            if (prevPrev && prev.position - prevPrev.position < 1e-10) {
                rebalance(items, index, index - 1);
                return;
            }

            const newPosition = prevPrev
                ? (prevPrev.position + prev.position) / 2
                : prev.position - 1;
            upsert({
                variables: {
                    productLine: { id: item.id, position: newPosition },
                },
            });
        } else {
            const next = items[index + 1];
            const nextNext = items[index + 2];

            if (nextNext && nextNext.position - next.position < 1e-10) {
                rebalance(items, index, index + 1);
                return;
            }

            const newPosition = nextNext
                ? (next.position + nextNext.position) / 2
                : next.position + 1;
            upsert({
                variables: {
                    productLine: { id: item.id, position: newPosition },
                },
            });
        }
    }

    async function handleSelect(id: string) {
        if (productLineID === id) return;
        if (await confirmUnsavedChanges(guard)) navigate(id);
    }

    return (
        <>
            <UI.Button
                type="button"
                onClick={handlePurge}
                disabled={!unusedLines.length}
                style={unusedLines.length
                    ? { background: "red", borderColor: "red", color: "#FFF" }
                    : undefined}
            >
                {unusedLines.length
                    ? `Purge ${unusedLines.length} unused product line(s)`
                    : "No unused product lines to purge"}
            </UI.Button>
            {!productLines.length
                ? <span>no product lines</span>
                : (
                    <ol>
                        {productLines.map((productLine) => {
                            const index = allProductLines.indexOf(productLine);
                            const count = countOf(productLine);
                            const deletable = countsReady && count === 0;
                            return (
                                <Row key={productLine.id}>
                                    <a
                                        href="#"
                                        onClick={(e) => { e.preventDefault(); handleSelect(productLine.id); }}
                                    >
                                        {translate(productLine.name) || "unnamed product line"}
                                    </a>
                                    {countsReady && <Count>{count} products</Count>}
                                    <OrderButton
                                        disabled={index === 0}
                                        onClick={() => move(index, "up")}
                                        type="button"
                                    >
                                        ▲
                                    </OrderButton>
                                    <OrderButton
                                        disabled={index === allProductLines.length - 1}
                                        onClick={() => move(index, "down")}
                                        type="button"
                                    >
                                        ▼
                                    </OrderButton>
                                    {deletable && (
                                        <DeleteButton
                                            onClick={() => handleDelete(productLine)}
                                            title="delete unused product line"
                                            type="button"
                                        >
                                            <IoTrashOutline />
                                        </DeleteButton>
                                    )}
                                </Row>
                            );
                        })}
                    </ol>
                )}
        </>
    );
}
